import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import apiClient from '../../services/apiClient';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import { commonTableStyles } from '../../components/common/tableStyles';
import { MRT_FullScreenToggleButton, MRT_GlobalFilterTextField, MRT_ShowHideColumnsButton, MRT_ToggleDensePaddingButton, MRT_ToggleFiltersButton, MRT_ToggleGlobalFilterButton } from 'material-react-table';

const asArray = (value) => (Array.isArray(value) ? value : []);
const formatBool = (value) => (value === true ? 'Yes' : value === false ? 'No' : value || '-');
const formatValue = (value) => {
  if (value === true || value === false) return formatBool(value);
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  return value ?? '-';
};
const formatZip = (zipCd) => {
  if (Array.isArray(zipCd)) {
    const cleaned = zipCd.filter((z) => z && z !== '-');
    return cleaned.join('-');
  }
  return zipCd || '-';
};

const formatValueStr = (value) => {
  if (value === true || value === false) return formatBool(value);
  if (Array.isArray(value)) return value.length ? value.join(', ') : '';
  return value ?? '';
};

const formatTableData = (state, ref, otherData = []) => {
  if (!state) return [];
  if (Array.isArray(state)) return state;
  if (typeof state !== 'object') return [];

  const array = [];
  Object.keys(state).forEach((i) => {
    if (ref === 'isn') {
      Object.keys(state[i] || {}).forEach((j) => {
        const isn = { ...(state[i]?.[j] || {}) };
        isn.Id = j;
        isn.isnCode = i;
        array.push(isn);
      });
    } else if (ref === 'sln') {
      const sln = { ...(state[i] || {}) };
      sln.slnAuthNbr = i;
      array.push(sln);
    } else if (ref === 'addr') {
      const addr = { ...(state[i] || {}) };
      addr.addrId = i;

      if (Array.isArray(otherData) && otherData.length > 0) {
        addr.overriddenAddressFields = [];
        otherData.forEach((item) => {
          if (typeof item !== 'string') return;
          if (item.length === 41 && addr.addrId === item.substring(5)) {
            addr.overriddenAddress = item.substring(5);
          } else if (item.length > 41 && addr.addrId === item.substring(5, 41)) {
            addr.overriddenAddressFields.push(item.substring(42));
          }
        });
      }

      array.push(addr);
    } else if (ref === 'spc') {
      const spc = { ...(state[i] || {}) };
      spc.rank = i;
      array.push(spc);
    }
  });

  return array;
};

const TabPanel = ({ value, index, children }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 0 }}>{children}</Box>}
  </div>
);

TabPanel.propTypes = {
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  children: PropTypes.node,
};

TabPanel.defaultProps = {
  children: null,
};

const DetailItem = ({ label, value, overridden }) => {
  const isStatus = label === 'Status';
  const isPrimarySpecialty = label === 'Primary Specialty';
  const isNpi = label === 'NPI Id';

  const statusVal = String(value || '').toLowerCase();
  const isActive = statusVal === 'active' || statusVal === 'a' || statusVal === 'true';

  let displayValue = formatValueStr(value);
  if (displayValue === '-' || displayValue === 'N/A' || displayValue === '') {
    displayValue = '';
  }
  return (
    <Box
      sx={{
        px: 0,
        py: 0,
        backgroundColor: overridden ? alpha('#1E88E5', 0.04) : 'transparent',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: '#64748b',
          
          textTransform: 'none',
          display: 'block',
          mb: 0.25,
        }}
      >
        {label}
        {overridden ? (
          <Box
            component="span"
            sx={{
              ml: 0.5,
              color: '#1E88E5',
              fontWeight: 800,
              
            }}
          >
            *
          </Box>
        ) : null}
      </Typography>

      {!displayValue ? (
        <Typography
          variant="body2"
          sx={{
            color: '#94a3b8',
            
            fontWeight: 400,
          }}
        >
          -
        </Typography>
      ) : isStatus ? (
        <Chip
          label={displayValue}
          size="small"
          sx={{
            backgroundColor: isActive ? '#dcfce7' : '#f1f5f9',
            color: isActive ? '#166534' : '#475569',
            fontWeight: 600,
            
            borderRadius: '16px',
            height: '24px',
          }}
        />
      ) : isPrimarySpecialty ? (
        <Typography
          variant="body2"
          sx={{
            color: '#2563eb',
            fontWeight: 600,
            letterSpacing: "0.75px",
            
          }}
        >
          {displayValue}
        </Typography>
      ) : (
        <Typography
          variant="body2"
          sx={{
            color: '#1f2937',
            fontWeight: 500,
            
            lineHeight: 1.4,
          }}
        >
          {displayValue}
        </Typography>
      )}
    </Box>
  );
};

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  overridden: PropTypes.bool,
};

DetailItem.defaultProps = {
  value: '-',
  overridden: false,
};

const DetailsPageLoader = () => (
  <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Skeleton variant="text" width={180} height={36} />
        <Skeleton variant="text" width={220} height={22} />
      </Box>
      <Skeleton variant="rounded" width={110} height={36} />
    </Box>

    <Box sx={{ px: 3, py: 2 }}>
      {Array.from({ length: 3 }).map((_, sectionIndex) => (
        <Box key={sectionIndex} sx={{ my: 1, pt: sectionIndex === 0 ? 0 : 2, borderTop: sectionIndex === 0 ? 'none' : '1px solid', borderColor: 'divider' }}>
          <Skeleton variant="text" width={180} height={30} sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {Array.from({ length: 8 }).map((__, itemIndex) => (
              <Grid key={itemIndex} item xs={12} md={2}>
                <Skeleton variant="text" width="60%" height={18} />
                <Skeleton variant="text" width="85%" height={28} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Skeleton variant="text" width={160} height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={48} sx={{ mb: 1.5 }} />
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={38} sx={{ mb: 1 }} />
        ))}
      </Box>
    </Box>
  </Paper>
);

const SummaryRow = ({ items }) => {
  const chunkedRows = [];

  for (let index = 0; index < items.length; index += 6) {
    chunkedRows.push(items.slice(index, index + 6));
  }

  return (
    <Box>
      {chunkedRows.map((rowItems, rowIndex) => (
        <Box key={`summary-row-${rowIndex}`}>
          <Box
            sx={{
              display: 'grid',
              px: 1,
              gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
              gap: 1.5,
              mb: rowIndex === chunkedRows.length - 1 ? 0 : 2,
            }}
          >
            {rowItems.map((item) => (
              <Box key={item.label}>
                <DetailItem
                  label={item.label}
                  value={item.value}
                  overridden={item.overridden}
                />
              </Box>
            ))}
          </Box>
          {/* {rowIndex < chunkedRows.length - 1 ? <Divider sx={{ my: 0.5 }} /> : null} */}
        </Box>
      ))}
    </Box>
  );
};

const TabTableBox = ({ children }) => (
  <Box
    sx={{
      p: 1.5,
      border: '1px solid #e2e8f0',
      borderRadius: 1,
      backgroundColor: '#fff',
      overflow: 'hidden',
    }}
  >
    {children}
  </Box>
);

TabTableBox.propTypes = {
  children: PropTypes.node.isRequired,
};

SummaryRow.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.array,
      ]),
      overridden: PropTypes.bool,
      lg: PropTypes.number,
    })
  ).isRequired,
};

const HCPDetails = () => {
  const navigate = useNavigate();
  const { hcpId } = useParams();
  const { loading, error, data, execute } = useAPICall();
  const [tab, setTab] = useState(0);
  const [client, setClient] = useState({});
  const [paginationByTable, setPaginationByTable] = useState({
    address: { page: 1, pageSize: 10 },
    specialty: { page: 1, pageSize: 10 },
    subSpecialty: { page: 1, pageSize: 10 },
    sln: { page: 1, pageSize: 10 },
    isn: { page: 1, pageSize: 10 },
  });

  const details = useMemo(() => data?.data || data || {}, [data]);
  const clientName = client?.name || '';

  const normalizedDetails = useMemo(() => {
    const normalized = { ...details };
    const overridden = Array.isArray(normalized.overriddenFields) ? normalized.overriddenFields : [];

    if (normalized.primaryAddress?.zipCd && Array.isArray(normalized.primaryAddress.zipCd)) {
      const zip = normalized.primaryAddress.zipCd;
      if (zip[1] === '-') {
        normalized.primaryAddress.zipCd = zip.filter((z) => z !== '-');
      }
    }

    normalized.isn = formatTableData(normalized.isn, 'isn');
    normalized.slnDetails = formatTableData(normalized.slnDetails, 'sln');
    normalized.specialtyDetails = formatTableData(normalized.specialtyDetails, 'spc');
    normalized.subSpecialtyDetails = formatTableData(normalized.subSpecialtyDetails, 'spc');

    const addrOverrides = clientName === 'Avanir' ? overridden : [];
    normalized.addressDetails = formatTableData(normalized.addressDetails, 'addr', addrOverrides);

    return normalized;
  }, [details, clientName]);

  const overriddenFields = useMemo(
    () => new Set(normalizedDetails?.overriddenFields || []),
    [normalizedDetails]
  );
  const primaryAddress = useMemo(
    () => normalizedDetails?.primaryAddress || {},
    [normalizedDetails]
  );

  const relatedTableInitialState = useMemo(() => ({
    // showGlobalFilter: false,
    // showColumnFilters: false,
  }), []);

  const setRelatedPage = useCallback((tableKey, nextPage) => {
    setPaginationByTable((prev) => ({
      ...prev,
      [tableKey]: {
        ...prev[tableKey],
        page: nextPage,
      },
    }));
  }, []);

  const setRelatedPageSize = useCallback((tableKey, nextPageSize) => {
    setPaginationByTable((prev) => ({
      ...prev,
      [tableKey]: {
        ...prev[tableKey],
        pageSize: nextPageSize,
        page: prev[tableKey].pageSize === nextPageSize ? prev[tableKey].page : 1,
      },
    }));
  }, []);
  useEffect(() => {
    const load = async () => {
      try {
        await execute(API_CONFIG.hcp.details, null, {
          params: { mdmId: hcpId },
          errorMessage: 'Unable to load HCP details.',
        });
      } catch {
      }
    };

    load();
  }, [execute, hcpId]);

  useEffect(() => {
    const loadClient = async () => {
      try {
        const response = await apiClient.get(API_CONFIG.auth.getClient.endpoint);
        setClient(response?.data || {});
      } catch {
        setClient({});
      }
    };
    loadClient();
  }, []);

  const summaryRows = useMemo(() => {
    const rows = [
      [
        { label: 'MDM Id', value: normalizedDetails.mdmId || hcpId, overridden: overriddenFields.has('mdmId') },
        ...(clientName === 'Avanir' ? [{ label: 'Prof Id', value: normalizedDetails.profId }] : []),
        { label: 'Onekey Individual Id', value: normalizedDetails.okIndvId },
        { label: 'Source', value: normalizedDetails.source },
        {
          label: 'Full Name',
          value: [
            normalizedDetails.firstName,
            normalizedDetails.middleName,
            normalizedDetails.lastName,
          ].filter(Boolean).join(' ') || '-',
        },
        { label: 'First Name', value: normalizedDetails.firstName, overridden: overriddenFields.has('firstName') },
        { label: 'Middle Name', value: normalizedDetails.middleName, overridden: overriddenFields.has('middleName') },
        { label: 'Last Name', value: normalizedDetails.lastName, overridden: overriddenFields.has('lastName') },
        { label: 'Gender', value: normalizedDetails.gender, overridden: overriddenFields.has('gender') },
        { label: 'Birth Year', value: normalizedDetails.birthYr, overridden: overriddenFields.has('birthYr') },
        { label: 'Address Rank', value: primaryAddress.rank ?? '-' },
        { label: 'Address Line 1', value: primaryAddress.addrLn1 },
        { label: 'Address Line 2', value: primaryAddress.addrLn2 },
        { label: 'City', value: primaryAddress.cityNm },
        { label: 'State', value: primaryAddress.stCd },
        { label: 'Zip', value: formatZip(primaryAddress.zipCd) },
        { label: 'Primary Profession', value: normalizedDetails.primaryProfession, overridden: overriddenFields.has('primaryProfession') },
        { label: 'Primary Specialty', value: normalizedDetails.primarySpecialty, overridden: overriddenFields.has('primarySpecialty') },
        { label: 'Full Profile', value: normalizedDetails.isGoldenUniverse },
        { label: 'NPI Id', value: normalizedDetails.npiId, overridden: overriddenFields.has('npiId') },
        { label: 'Status', value: normalizedDetails.status, overridden: overriddenFields.has('status') },
        { label: 'Created Date', value: normalizedDetails.createdDate },
        { label: 'Updated Date', value: normalizedDetails.verifiedDate },
        { label: 'MDM Created Time', value: normalizedDetails.mdmCreatedTime },
        { label: 'MDM Updated Time', value: normalizedDetails.mdmUpdatedTime },
        { label: 'AMA PDRP', value: normalizedDetails.amaPdrpInd },
        ...(clientName === 'Avanir'
          ? [
            { label: 'Has Federal Sanction', value: normalizedDetails.hasFederalSanction },
            { label: 'Sanction Source', value: normalizedDetails.sanctionSource, lg: 4 },
          ]
          : []),
        { label: 'Is Eligible', value: normalizedDetails.isEligible },
        ...(clientName === 'Avanir'
          ? [{ label: 'Avanir Do Not Call', value: normalizedDetails.avnrDoNotCall }]
          : []),
        ...(clientName === 'Leo'
          ? [{ label: 'Legal Do Not Call', value: normalizedDetails.legalDoNotCall }]
          : []),
        { label: 'AMA Do Not Call', value: normalizedDetails.amaNoCtctInd },
        { label: 'PMC Cust Id', value: normalizedDetails.pmcCustId },
        ...(clientName === 'Avanir'
          ? [{ label: 'Is Sampleable', value: normalizedDetails.isSampleable }]
          : []),
      ],
    ];

    return rows;
  }, [clientName, hcpId, normalizedDetails, overriddenFields, primaryAddress]);

  const sharedTableProps = {
    initialState: relatedTableInitialState,
    autoResetPageIndex: false,
    manualPagination: false,
  };

  const renderTableTabs = () => (
    <Tabs
      value={tab}
      onChange={(e, v) => setTab(v)}
      variant="scrollable"
      scrollButtons="auto"
      sx={{
        minHeight: 48,
        '& .MuiTabs-indicator': {
          backgroundColor: '#1E88E5',
          height: 2.5,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
        },
        '& .MuiTabs-flexContainer': {
          gap: 0.2,
        },
      }}
    >
      {['Address', 'Specialty', 'SLN', 'ISN'].map((label) => (
        <Tab
          key={label}
          label={label}
          disableRipple
          sx={{
            minHeight: 48,
            px: 2,
            textTransform: 'none',
            fontWeight: 600,
            
            color: '#64748b',
            '&.Mui-selected': { color: '#1E88E5' },
          }}
        />
      ))}
    </Tabs>
  );



  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Box sx={{ mb: 1.5 }}>
        <PageBreadcrumbs items={[
          { label: 'HCP Search', path: '/search/hcp' },
          { label: 'HCP Details' }
        ]} />
      </Box>

      {loading ? (
        <DetailsPageLoader />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Top Card: HCP Information */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              backgroundColor: '#fff',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid #E5E7EB',
                borderLeft: '4px solid #1E88E5',
                display: 'flex',
                background: "#fbfdfe",
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B',  mb: 0.25, lineHeight: 1.2 }}>
                  HCP Details
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b',  fontWeight: 500 }}>
                  Record details for HCP ID: <Box component="span" sx={{ color: '#1E88E5', fontWeight: 600 }}>{hcpId}</Box>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {overriddenFields.has('mdmId') ? (
                  <Chip
                    label="Account Overridden"
                    size="small"
                    sx={{
                      backgroundColor: alpha('#ef4444', 0.1),
                      color: '#b91c1c',
                      fontWeight: 700,
                    }}
                  />
                ) : null}
                <Button className="action-btn process-btn action-btn process-btn"
                  variant="contained"
                  // size="small"
                  disableElevation
                  onClick={() => navigate(`/hcp-dcr?mdmId=${hcpId}`)}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    
                    px: 2.5,
                    py: 0.75,
                    backgroundColor: '#1E88E5',
                    '&:hover': { backgroundColor: '#1565C0' },
                  }}
                >
                  Edit HCP
                </Button>
              </Box>
            </Box>

            <Box sx={{ p: 1.25 }}>
              {summaryRows.map((row, index) => (
                <Box key={`summary-row-${index}`} sx={{ py: index === 0 ? 0 : 0.5 }}>
                  <SummaryRow items={row} />
                  {index < summaryRows.length - 1 ? <Divider sx={{ mt: 0.5 }} /> : null}
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Bottom Card: Tables & Source Records */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              backgroundColor: '#fff',
              overflow: 'hidden'
            }}
          >
            <TabPanel value={tab} index={0}>
              <Box sx={{p:0 }}>
                <GlobalMaterialTable
                  columns={[
                    { header: 'Source', id: 'source', accessorFn: (row) => row.source || '-' },
                    { header: 'Master Primary', id: 'masterPrimary', accessorFn: (row) => formatBool(row.masterPrimary) },
                    { header: 'External Id', id: 'externalId', accessorFn: (row) => row.externalId || '-' },
                    { header: 'Veeva Primary', id: 'veevaPrimary', accessorFn: (row) => formatBool(row.primary) },
                    { header: 'Client Preferred', id: 'clientPreferred', accessorFn: (row) => formatBool(row.clientPreferred) },
                    { header: 'Ok Best', id: 'okBest', accessorFn: (row) => formatBool(row.okBestAddr) },
                    { header: 'Rank', id: 'rank', accessorFn: (row) => row.rank ?? '-' },
                    { header: 'Address Line 1', id: 'addr1', accessorFn: (row) => row.addrLn1 || '-' },
                    { header: 'Address Line 2', id: 'addr2', accessorFn: (row) => row.addrLn2 || '-' },
                    { header: 'City', id: 'city', accessorFn: (row) => row.cityNm || '-' },
                    { header: 'State', id: 'state', accessorFn: (row) => row.stCd || '-' },
                    { header: 'Zip', id: 'zip', accessorFn: (row) => formatZip(row.zipCd) },
                    { header: 'Address Id', id: 'addressId', accessorFn: (row) => row.addrId || '-' },
                    { header: 'Status', id: 'status', accessorFn: (row) => row.addressStatusLabel || row.statusLabel || '-' },
                    { header: 'Is Sampleable', id: 'isSampleable', accessorFn: (row) => formatBool(row.isSampleable) },
                    { header: 'Setting', id: 'setting', accessorFn: (row) => row.setting || row.addressSetting || '-' },
                    { header: 'Category', id: 'category', accessorFn: (row) => row.category || row.addressCategory || '-' },
                    { header: 'Business Name', id: 'businessName', accessorFn: (row) => row.busNm || row.businessName || row.primaryBusinessName || '-' },
                    { header: 'Rep Managed', id: 'repManaged', accessorFn: (row) => formatBool(row.repManaged) },
                    { header: 'Affiliation Type', id: 'affiliationType', accessorFn: (row) => row.affltnType || row.affiliationType || '-' },
                    { header: 'Cell Phone', id: 'cellPhone', accessorFn: (row) => row.addressCellPhone || row.cellPhone || '-' },
                    { header: 'Telephone', id: 'telephone', accessorFn: (row) => row.addressTelephone || row.telephone || '-' },
                    { header: 'Fax', id: 'fax', accessorFn: (row) => row.fax || row.addressFax || '-' },
                    { header: 'Street Front Id', id: 'streetFrontId', accessorFn: (row) => row.strFrontId || row.streetFrontId || '-' },
                    { header: 'Latitude', id: 'latitude', accessorFn: (row) => row.latitude ?? '-' },
                    { header: 'Longitude', id: 'longitude', accessorFn: (row) => row.longitude ?? '-' },
                    { header: 'Overridden', id: 'overridden', accessorFn: (row) => formatBool(row.overridden) },
                  ]}
                  data={asArray(normalizedDetails.addressDetails)}
                  emptyMessage="No address details found"
                  page={paginationByTable.address.page}
                  pageSize={paginationByTable.address.pageSize}
                  onPageChange={(nextPage) => setRelatedPage('address', nextPage)}
                  onPageSizeChange={(nextPageSize) => setRelatedPageSize('address', nextPageSize)}
                  rowCount={asArray(normalizedDetails.addressDetails).length}
                  OuterBorder={"none"}
                  renderTopToolbarCustomActions={renderTableTabs}

                   renderToolbarInternalActions={({ table }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MRT_ToggleGlobalFilterButton table={table} />
                    <MRT_ToggleFiltersButton table={table} />
                    <MRT_ShowHideColumnsButton table={table} />
                    <MRT_ToggleDensePaddingButton table={table} />
                    <MRT_FullScreenToggleButton table={table} />
                         <Button className="action-btn process-btn action-btn process-btn"
                    variant="contained"
                    disableElevation
                    onClick={() => navigate(`/details/hcp/${hcpId}/source-records`)}
                    sx={{
                      alignSelf: { xs: 'flex-start', md: 'center' },
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      
                      px: 2.5,
                      py: 0.75,
                      backgroundColor: '#1E88E5',
                      '&:hover': { backgroundColor: '#1565C0' },
                    }}
                  >
                    Source Records
                  </Button>
                  </Box>
                  )}
                  layoutMode="grid"
                />
              </Box>
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <Box sx={{ p:0 }}>
                <GlobalMaterialTable
                  columns={[
                    { header: 'Rank', id: 'rank', accessorFn: (row) => row.rank ?? '-' },
                    { header: 'Specialty Description', id: 'spclDesc', accessorFn: (row) => row.spclDesc || '-' },
                    { header: 'Specialty Code', id: 'spclCode', accessorFn: (row) => row.spclCode || '-' },
                    { header: 'Specialty Group', id: 'spclGroup', accessorFn: (row) => row.clientSpclGroup || '-' },
                    { header: 'Professional Description', id: 'prfsnDesc', accessorFn: (row) => row.prfsnDesc || '-' },
                  ]}
                  data={asArray(normalizedDetails.specialtyDetails)}
                  emptyMessage="No specialty details found"
                  page={paginationByTable.specialty.page}
                  pageSize={paginationByTable.specialty.pageSize}
                  onPageChange={(nextPage) => setRelatedPage('specialty', nextPage)}
                  onPageSizeChange={(nextPageSize) => setRelatedPageSize('specialty', nextPageSize)}
                  rowCount={asArray(normalizedDetails.specialtyDetails).length}
                  OuterBorder={"none"}
                  OuterBoxShadow={"none"}
                  renderTopToolbarCustomActions={renderTableTabs}
                   renderToolbarInternalActions={({ table }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,p:0 }}>
                    <MRT_GlobalFilterTextField table={table} />
                    <MRT_ToggleGlobalFilterButton table={table} />
                    <MRT_ToggleFiltersButton table={table} />
                    <MRT_ShowHideColumnsButton table={table} />
                    <MRT_ToggleDensePaddingButton table={table} />
                    <MRT_FullScreenToggleButton table={table} />
                         <Button className="action-btn process-btn action-btn process-btn"
                    variant="contained"
                    disableElevation
                    onClick={() => navigate(`/details/hcp/${hcpId}/source-records`)}
                    sx={{
                      alignSelf: { xs: 'flex-start', md: 'center' },
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      
                      px: 2.5,
                      py: 0.75,
                      backgroundColor: '#1E88E5',
                      '&:hover': { backgroundColor: '#1565C0' },
                    }}
                  >
                    Source Records
                  </Button>
                  </Box>
                  )}
                />
              </Box>
              <Divider sx={{ my: 0, mx: 0, borderColor: '#e2e8f0' }} />

              <Box sx={{ p:0, mt: 0 }}>
                <GlobalMaterialTable
                  tableTitle={
                      <Box sx={{ ml: 0.5 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: '#0f172a',
                            fontWeight: 700,
                          }}
                        >
                          Subspecialty
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#94a3b8',
                            fontSize: '12px',
                            fontWeight: 400,
                          }}
                        >
                          Specialty classification
                        </Typography>
                      </Box>
                    }
                  columns={[
                    { header: 'Rank', id: 'rank', accessorFn: (row) => row.rank ?? '-' },
                    { header: 'Specialty Code', id: 'spclCd', accessorFn: (row) => row.spclCd || '-' },
                    { header: 'Specialty Description', id: 'spclDesc', accessorFn: (row) => row.spclDesc || '-' },
                    { header: 'Subspecialty Code', id: 'subSpclCode', accessorFn: (row) => row.subSpclCode || '-' },
                    { header: 'Subspecialty Description', id: 'subSpclDesc', accessorFn: (row) => row.subSpclDesc || '-' },
                  ]}
                  data={asArray(normalizedDetails.subSpecialtyDetails)}
                  emptyMessage="No subspecialty details found"
                  page={paginationByTable.subSpecialty.page}
                  pageSize={paginationByTable.subSpecialty.pageSize}
                  onPageChange={(nextPage) => setRelatedPage('subSpecialty', nextPage)}
                  onPageSizeChange={(nextPageSize) => setRelatedPageSize('subSpecialty', nextPageSize)}
                  rowCount={asArray(normalizedDetails.subSpecialtyDetails).length}
                  OuterBorder={"none"}
                   enableTopToolbar={true}
                    initialState={{
                    showGlobalFilter: true,
                  }}
                />
              </Box>
            </TabPanel>

            <TabPanel value={tab} index={2}>
              <Box sx={{p:0 }}>
                <GlobalMaterialTable
                  columns={[
                    { header: 'Auth Number', id: 'authNumber', accessorFn: (row) => row.slnAuthNbr || '-' },
                    { header: 'Status', id: 'status', accessorFn: (row) => row.authnStatDesc || '-' },
                    { header: 'State Code', id: 'stateCode', accessorFn: (row) => row.stCd || '-' },
                    { header: 'Board ID', id: 'boardId', accessorFn: (row) => row.stBrdId || '-' },
                    { header: 'Board Name', id: 'boardName', accessorFn: (row) => row.stBrdNm || '-' },
                    { header: 'Type ID', id: 'typeId', accessorFn: (row) => row.stLicTypId || '-' },
                    { header: 'Type Name', id: 'typeName', accessorFn: (row) => row.stLicTypNm || '-' },
                    { header: 'Privilege ID', id: 'privilegeId', accessorFn: (row) => row.stLicPrivlId || '-' },
                    { header: 'Privilege', id: 'privilege', accessorFn: (row) => row.stLicPrivlNm || '-' },
                    { header: 'Certification Name', id: 'certName', accessorFn: (row) => row.stLicCertNm || '-' },
                    { header: 'Privilege Rank', id: 'privilegeRank', accessorFn: (row) => row.stLicTypPrivlRnkNbr || '-' },
                    { header: 'Effective Date', id: 'effDate', accessorFn: (row) => row.authnEffDt || '-' },
                    { header: 'Expiration Date', id: 'expDate', accessorFn: (row) => row.authnTrmtnDt || '-' },
                    { header: 'Grace Period', id: 'gracePeriod', accessorFn: (row) => row.gracePeriod || '-' },
                    { header: 'Key', id: 'key', accessorFn: (row) => row.slnKeyNbr || '-' },
                    { header: 'Status Updated Date', id: 'statusUpdated', accessorFn: (row) => row.authnStatDt || '-' },
                    { header: 'Deactivation Reason', id: 'deactReason', accessorFn: (row) => row.stLicDeactvtnRsnDesc || '-' },
                  ]}
                  data={asArray(normalizedDetails.slnDetails)}
                  emptyMessage="No SLN details found"
                  page={paginationByTable.sln.page}
                  pageSize={paginationByTable.sln.pageSize}
                  onPageChange={(nextPage) => setRelatedPage('sln', nextPage)}
                  onPageSizeChange={(nextPageSize) => setRelatedPageSize('sln', nextPageSize)}
                  rowCount={asArray(normalizedDetails.slnDetails).length}
                    OuterBorder={"none"}
                  renderTopToolbarCustomActions={renderTableTabs}
                   renderToolbarInternalActions={({ table }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MRT_ToggleGlobalFilterButton table={table} />
                    <MRT_ToggleFiltersButton table={table} />
                    <MRT_ShowHideColumnsButton table={table} />
                    <MRT_ToggleDensePaddingButton table={table} />
                    <MRT_FullScreenToggleButton table={table} />
                         <Button className="action-btn process-btn action-btn process-btn"
                    variant="contained"
                    disableElevation
                    onClick={() => navigate(`/details/hcp/${hcpId}/source-records`)}
                    sx={{
                      alignSelf: { xs: 'flex-start', md: 'center' },
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      
                      px: 2.5,
                      py: 0.75,
                      backgroundColor: '#1E88E5',
                      '&:hover': { backgroundColor: '#1565C0' },
                    }}
                  >
                    Source Records
                  </Button>
                  </Box>
                  )}
                />
              </Box>
            </TabPanel>

            <TabPanel value={tab} index={3}>
              <Box sx={{p:0 }}>
                <GlobalMaterialTable
                  columns={[
                    { header: 'Code', id: 'code', accessorFn: (row) => row.isnCode || '-' },
                    { header: 'Id', id: 'id', accessorFn: (row) => row.Id || '-' },
                    { header: 'Status', id: 'status', accessorFn: (row) => row.status || '-' },
                    { header: 'Effective Date', id: 'effDate', accessorFn: (row) => row.effDate || '-' },
                    { header: 'Termination Date', id: 'termDate', accessorFn: (row) => row.termnDate || '-' },
                    { header: 'Deactivation Reason', id: 'deactReason', accessorFn: (row) => row.deactvtnRsn || '-' },
                  ]}
                  data={asArray(normalizedDetails.isn)}
                  emptyMessage="No ISN details found"
                  page={paginationByTable.isn.page}
                  pageSize={paginationByTable.isn.pageSize}
                  onPageChange={(nextPage) => setRelatedPage('isn', nextPage)}
                  onPageSizeChange={(nextPageSize) => setRelatedPageSize('isn', nextPageSize)}
                  rowCount={asArray(normalizedDetails.isn).length}
                    OuterBorder={"none"}
                  renderTopToolbarCustomActions={renderTableTabs}
                   renderToolbarInternalActions={({ table }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MRT_ToggleGlobalFilterButton table={table} />
                    <MRT_ToggleFiltersButton table={table} />
                    <MRT_ShowHideColumnsButton table={table} />
                    <MRT_ToggleDensePaddingButton table={table} />
                    <MRT_FullScreenToggleButton table={table} />
                         <Button className="action-btn process-btn action-btn process-btn"
                    variant="contained"
                    disableElevation
                    onClick={() => navigate(`/details/hcp/${hcpId}/source-records`)}
                    sx={{
                      alignSelf: { xs: 'flex-start', md: 'center' },
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      
                      px: 2.5,
                      py: 0.75,
                      backgroundColor: '#1E88E5',
                      '&:hover': { backgroundColor: '#1565C0' },
                    }}
                  >
                    Source Records
                  </Button>
                  </Box>
                  )}
                />
              </Box>
            </TabPanel>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default HCPDetails;
